import { Box, Button as MuiButton } from '@mui/material'
import { ComponentProps } from 'react'
import { Icon } from './Icon'
import { Typography } from './Typography'
import { Colors } from './color'

const colorMap: Record<
  string,
  Record<
    string,
    {
      background: keyof typeof Colors
      color: keyof typeof Colors
      hover: { background: keyof typeof Colors; color: keyof typeof Colors }
      ripple: { color: keyof typeof Colors }
      disabled: {
        background: keyof typeof Colors
        color: keyof typeof Colors
        borderColor: keyof typeof Colors
      }
    }
  >
> = {
  primary: {
    contained: {
      background: 'Samansa_Yellow/500',
      color: 'Base/Black',
      hover: {
        background: 'Samansa_Yellow/600',
        color: 'Gray/700',
      },
      ripple: {
        color: 'Base/White',
      },
      disabled: {
        background: 'Gray/600',
        color: 'Gray/450',
        borderColor: 'Transparent',
      },
    },
    outlined: {
      background: 'Transparent',
      color: 'Samansa_Yellow/500',
      hover: {
        background: 'Samansa_Yellow/Alpha/16',
        color: 'Samansa_Yellow/500',
      },
      ripple: {
        color: 'Samansa_Yellow/Alpha/32',
      },
      disabled: {
        background: 'Transparent',
        color: 'Gray/800',
        borderColor: 'Gray/800',
      },
    },
    text: {
      background: 'Transparent',
      color: 'Samansa_Yellow/500',
      hover: {
        background: 'Samansa_Yellow/Alpha/16',
        color: 'Samansa_Yellow/500',
      },
      ripple: {
        color: 'Samansa_Yellow/Alpha/32',
      },
      disabled: {
        background: 'Transparent',
        color: 'Gray/800',
        borderColor: 'Transparent',
      },
    },
  },
  secondary: {
    contained: {
      background: 'Gray/800',
      color: 'Base/White',
      hover: {
        background: 'Gray/900',
        color: 'Base/White',
      },
      ripple: {
        color: 'Base/White',
      },
      disabled: {
        background: 'Gray/600',
        color: 'Gray/450',
        borderColor: 'Transparent',
      },
    },
    outlined: {
      background: 'Transparent',
      color: 'Gray/600',
      hover: {
        background: 'White/Alpha/08',
        color: 'Gray/600',
      },
      ripple: {
        color: 'White/Alpha/32',
      },
      disabled: {
        background: 'White/Alpha/16',
        color: 'Gray/600',
        borderColor: 'White/Alpha/32',
      },
    },
    text: {
      background: 'Transparent',
      color: 'Gray/600',
      hover: {
        background: 'White/Alpha/16',
        color: 'Gray/600',
      },
      ripple: {
        color: 'White/Alpha/32',
      },
      disabled: {
        background: 'Transparent',
        color: 'Gray/800',
        borderColor: 'Transparent',
      },
    },
  },
} as const

type BaseProps = {
  color?: 'primary' | 'secondary'
  variant?: 'contained' | 'outlined' | 'text'
  size?: 'small' | 'medium' | 'large'
  disabled?: boolean
  fullWidth?: boolean
  startIcon?: ComponentProps<typeof Icon>['name']
  endIcon?: ComponentProps<typeof Icon>['name']
  children: string
}

type Props = BaseProps &
  (
    | {
        type?: 'button'
        onClick: React.MouseEventHandler<HTMLButtonElement>
      }
    | {
        type: 'submit' | 'reset'
        onClick?: never
      }
  )

export const Button = ({
  variant = 'contained',
  size = 'medium',
  type = 'button',
  color = 'primary',
  disabled = false,
  fullWidth = false,
  startIcon,
  endIcon,
  children,
  onClick,
}: Props) => {
  return (
    <MuiButton
      variant={variant}
      type={type}
      color={color}
      onClick={onClick}
      size={size}
      disabled={disabled}
      fullWidth={fullWidth}
      sx={{
        borderRadius: '100px',
        transition: 'all 0.2s cubic-bezier(0.3, 0.3, 0.3, 1)',
        backgroundColor: colorMap[color][variant].background
          ? Colors[colorMap[color][variant].background]
          : undefined,
        color: Colors[colorMap[color][variant].color],
        borderColor: Colors[colorMap[color][variant].color],
        minHeight: '0',
        '&:hover': {
          backgroundColor: colorMap[color][variant].hover.background
            ? Colors[colorMap[color][variant].hover.background]
            : undefined,
          color: Colors[colorMap[color][variant].hover.color],
          borderColor: Colors[colorMap[color][variant].hover.color],
        },
        '&.MuiTouchRipple-root': {
          color: Colors[colorMap[color][variant].ripple.color],
        },
        '&.Mui-disabled': {
          backgroundColor: Colors[colorMap[color][variant].disabled.background],
          color: Colors[colorMap[color][variant].disabled.color],
          borderColor: Colors[colorMap[color][variant].disabled.borderColor],
        },
      }}
    >
      <Box display='flex' alignItems='center' gap={1}>
        {startIcon && (
          <Icon name={startIcon} size={size === 'small' ? 16 : size === 'medium' ? 20 : 22} />
        )}
        <Typography
          name={
            size === 'small'
              ? 'Body/12/Regular'
              : size === 'medium'
              ? 'Body/14/Medium'
              : 'Body/16/Medium'
          }
        >
          {children}
        </Typography>
        {endIcon && (
          <Icon name={endIcon} size={size === 'small' ? 16 : size === 'medium' ? 20 : 22} />
        )}
      </Box>
    </MuiButton>
  )
}
